import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Lock, Calendar, MapPin, Shield, Camera, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { listHotels, getHotel, getHotelUpdates, startCheckout, fileUrl } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { track } from "@/lib/analytics";

export default function HotelPilot() {
  const params = useParams();
  const [hotel, setHotel] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    (async () => {
      let h = null;
      if (params.hotelId) {
        try {
          h = await getHotel(params.hotelId);
        } catch {
          /* ignore */
        }
      } else {
        const list = await listHotels();
        h = list[0] || null;
      }
      setHotel(h);
      if (h?.hotel_id) {
        try {
          const u = await getHotelUpdates(h.hotel_id);
          setUpdates(u);
        } catch {
          /* ignore */
        }
      }
    })();
  }, [params.hotelId]);

  useSeo({
    title: hotel ? `${hotel.name} — Progetto SuiteCrowd` : "Progetto — SuiteCrowd",
    description: hotel
      ? `Co-finanzia ${hotel.name} a ${hotel.location}. Quota ${hotel.quota}€ per un voucher di ${hotel.stay_days} giorni. Fondi in escrow, rimborso garantito.`
      : "Progetto SuiteCrowd: hotel 5 stelle in ristrutturazione.",
    image: hotel?.image_url ? fileUrl(hotel.image_url) : undefined,
  });

  if (!hotel) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-500 text-sm tracking-wider">
        Progetto non disponibile
      </div>
    );
  }

  const pct = Math.min(100, (hotel.raised_amount / hotel.goal_amount) * 100);

  const support = async () => {
    if (!email) {
      toast.error("Inserisci la tua email per continuare.");
      return;
    }
    setLoading(true);
    track("hotel_checkout_start", { hotel_id: hotel.hotel_id, amount: hotel.quota });
    try {
      const { checkout_url } = await startCheckout(hotel.hotel_id, email, window.location.origin);
      window.location.href = checkout_url;
    } catch {
      toast.error("Impossibile avviare il checkout. Riprova.");
      setLoading(false);
    }
  };

  const heroSrc = fileUrl(hotel.image_url) || "https://images.unsplash.com/photo-1673403731036-30ef7a4995f5?w=1600&q=80";

  return (
    <div className="min-h-screen bg-white text-[#121212]">
      <nav className="border-b border-[#E5E5E5] bg-white/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex justify-between items-center">
          <Logo variant="dark" size="md" />
          <Link
            to="/"
            data-testid="back-to-home"
            className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 hover:text-[#121212] inline-flex items-center gap-2 transition-colors"
          >
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section data-testid="hotel-hero" className="relative">
        <img src={heroSrc} alt={hotel.name} className="w-full h-[60vh] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="absolute bottom-10 left-6 md:left-12 right-6 md:right-12 text-white">
          <div className="sc-overline mb-3" style={{ color: "rgba(255,255,255,0.7)" }}>
            Progetto in raccolta
          </div>
          <h1
            data-testid="hotel-name"
            className="font-display font-light text-4xl md:text-6xl mb-3"
            style={{ letterSpacing: "-0.02em" }}
          >
            {hotel.name}
          </h1>
          <div className="flex items-center gap-2 text-white/80 text-sm">
            <MapPin size={14} /> {hotel.location}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-7 space-y-14">
          <div>
            <div className="sc-overline mb-4">Il Progetto</div>
            <p className="text-neutral-700 leading-[1.85] text-[15px] whitespace-pre-line">
              {hotel.description || "Descrizione in preparazione."}
            </p>
          </div>

          {hotel.gallery?.length > 0 && (
            <div>
              <div className="sc-overline mb-4">Galleria</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {hotel.gallery.map((path) => (
                  <button
                    key={path}
                    onClick={() => setLightbox(fileUrl(`/api/files/${path}`))}
                    className="aspect-square overflow-hidden group"
                    data-testid={`gallery-thumb-${path.slice(-10)}`}
                  >
                    <img
                      src={fileUrl(`/api/files/${path}`)}
                      alt="gallery"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-[#E5E5E5] pt-10 space-y-6">
            <div className="sc-overline">Regola del Voucher</div>
            <div className="space-y-4 text-[15px] text-neutral-700 leading-[1.8]">
              <p>
                Quota di sostegno di <strong className="text-[#0B192C]">{hotel.quota.toFixed(2).replace(".", ",")} €</strong>{" "}
                per un soggiorno di <strong>{hotel.stay_days} giorni</strong> in camera doppia, utilizzabile
                nell'anno solare successivo al completamento dei lavori.
              </p>
              <p>
                Validità limitata ai periodi di <strong>bassa e media stagione</strong> definiti dalla
                struttura. L'utilizzo in alta stagione o nei ponti festivi è possibile con un{" "}
                <strong>supplemento monetario</strong> obbligatorio, da saldare al momento della prenotazione.
              </p>
            </div>
          </div>

          <div className="border-t border-[#E5E5E5] pt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Lock, k: "Escrow", v: "Fondi congelati fino a target o avanzamento certificato" },
              { icon: Shield, k: "Rimborso", v: "Al 100% se il progetto non si completa nei tempi" },
              { icon: Calendar, k: "Voucher", v: `${hotel.stay_days} giorni · Anno successivo` },
            ].map(({ icon: Icon, k, v }, i) => (
              <div key={i} className="space-y-3">
                <Icon size={18} className="text-[#0B192C]" strokeWidth={1.2} />
                <div className="font-display font-normal text-sm tracking-wide">{k}</div>
                <p className="text-xs text-neutral-500 leading-relaxed">{v}</p>
              </div>
            ))}
          </div>

          {/* Timeline */}
          {updates.length > 0 && (
            <div className="border-t border-[#E5E5E5] pt-10">
              <div className="sc-overline mb-6 inline-flex items-center gap-2">
                <Camera size={13} /> Avanzamento Lavori
              </div>
              <div className="space-y-10">
                {updates.map((u, i) => (
                  <div
                    key={u.update_id}
                    data-testid={`timeline-item-${i}`}
                    className="grid grid-cols-1 md:grid-cols-12 gap-6"
                  >
                    <div className="md:col-span-5">
                      <img
                        src={fileUrl(u.image_url)}
                        alt="update"
                        className="w-full aspect-[4/3] object-cover cursor-pointer"
                        onClick={() => setLightbox(fileUrl(u.image_url))}
                      />
                    </div>
                    <div className="md:col-span-7 flex flex-col justify-center space-y-2">
                      <div className="sc-overline">
                        {new Date(u.created_at).toLocaleDateString("it-IT", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                        })}
                      </div>
                      <p className="text-[15px] text-neutral-700 leading-relaxed">{u.caption}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Aside */}
        <aside className="lg:col-span-5">
          <div
            data-testid="funding-card"
            className="border border-[#E5E5E5] p-10 md:p-12 sticky top-28 bg-white"
          >
            <div className="sc-overline mb-6">Avanzamento Raccolta</div>
            <div className="mb-6">
              <div className="flex items-baseline gap-3 flex-wrap">
                <span
                  data-testid="raised-amount"
                  className="font-display font-light text-4xl md:text-5xl text-[#0B192C]"
                  style={{ letterSpacing: "-0.02em" }}
                >
                  {hotel.raised_amount.toLocaleString("it-IT")} €
                </span>
                <span className="text-sm text-neutral-500">
                  di {hotel.goal_amount.toLocaleString("it-IT")} €
                </span>
              </div>
            </div>

            <div className="sc-progress-track mb-3">
              <div data-testid="progress-fill" className="sc-progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex justify-between text-[11px] tracking-[0.2em] uppercase text-neutral-500 mb-8">
              <span>{Math.round(pct)}% raggiunto</span>
              <span data-testid="days-left">{hotel.days_left} giorni rimasti</span>
            </div>

            <div className="border-t border-[#E5E5E5] pt-8 space-y-4">
              <div className="sc-overline">La tua quota</div>
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="font-display font-light text-3xl">
                    {hotel.quota.toFixed(2).replace(".", ",")} €
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">
                    per {hotel.stay_days} giorni · bassa/media stagione
                  </div>
                </div>
              </div>

              <input
                data-testid="checkout-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email per il voucher"
                className="sc-input-light mt-4"
              />

              <button
                data-testid="support-btn"
                onClick={support}
                disabled={loading}
                className="sc-btn-primary w-full mt-2"
              >
                {loading ? "Reindirizzamento..." : `Sostieni a ${hotel.quota.toFixed(2).replace(".", ",")} €`}
              </button>

              <p className="text-[11px] text-neutral-500 leading-relaxed mt-4">
                Pagamento gestito da Stripe. Fondi in escrow. Rimborso garantito se il progetto non si completa.
              </p>
            </div>
          </div>
        </aside>
      </section>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="lightbox"
        >
          <button
            className="absolute top-6 right-6 text-white/80 hover:text-white"
            onClick={() => setLightbox(null)}
            aria-label="Chiudi"
          >
            <X size={24} />
          </button>
          <img src={lightbox} alt="preview" className="max-h-[90vh] max-w-[90vw] object-contain" />
        </div>
      )}

      <Footer />
    </div>
  );
}
